import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { loadStripe } from "@stripe/stripe-js/pure";
import type {
  StripeCardCvcElement,
  StripeCardExpiryElement,
  StripeCardNumberElement,
} from "@stripe/stripe-js/types/stripe-js";
import numeral from "numeral";
import { twMerge } from "tailwind-merge";
import { useDebounce } from "usehooks-ts";

import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faSpinner } from "@fortawesome/free-solid-svg-icons/faSpinner";

import CustomCard from "components/CustomCard";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import useNotification from "components/Notification/useNotification";
import useAuthentication from "hooks/useAuthentication";
import useStripe from "hooks/useStripe";
import { fetchApi } from "utils/baseFetcher";
import { stripePublishableKey } from "utils/envVariables";
import { roboto } from "utils/fonts";
import smartkarmaPlus from "consts/plans/smartkarmaPlus";
import smartkarmaPro from "consts/plans/smartkarmaPro";

const TAX_RATE = 0.08; // TODO: get from BE
const TAX_COUNTRY: string[] = [];
const PHONE_NUMBER_REGEX = /\+?[\d\s]{1,14}$/;

const AccountUpgradeForm = ({
  className,
  hideOtherPlan,
  ...props
}: {
  className?: string;
  defaultPlan?: typeof smartkarmaPlus | typeof smartkarmaPro;
  hideOtherPlan?: boolean;
}) => {
  const { account } = useAuthentication();
  const { setNotification } = useNotification();

  const defaultPlan =
    props.defaultPlan ||
    (account?.previewPassInterest === "private_investor"
      ? smartkarmaPlus
      : smartkarmaPro);
  const [currentPlan, setCurrentPlan] = useState(defaultPlan);
  const [selectedOption, setSelectedOption] = useState(
    currentPlan.options?.[0]
  );
  const [couponCode, setCouponCode] = useState("");
  const debouncedCouponCode = useDebounce<string>(couponCode, 300);
  const [coupon, setCoupon] = useState<any>(null);
  const [couponError, setCouponError] = useState("");
  const [couponValidating, setCouponValidating] = useState(false);

  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState(false);

  const [cardNumberComplete, setCardNumberComplete] = useState(false);
  const [cardExpiryComplete, setCardExpiryComplete] = useState(false);
  const [cardCvcComplete, setCardCvcComplete] = useState(false);

  const [cardNumberError, setCardNumberError] = useState(false);
  const [carExpiryError, setCardExpiryError] = useState(false);
  const [cardCvcError, setCardCvcError] = useState(false);

  const [cardCountry, setCardCountry] = useState("");

  const [agreed, setAgreed] = useState(false);

  const cardNumberRef = useRef<HTMLDivElement>(null);
  const cardExpiryRef = useRef<HTMLDivElement>(null);
  const cardCvcRef = useRef<HTMLDivElement>(null);

  const [submiting, setSubmiting] = useState(false);

  const { stripe, setStripe } = useStripe();
  const stripeCardNumberRef = useRef<StripeCardNumberElement | null>(null);
  const stripeCardExpiryRef = useRef<StripeCardExpiryElement | null>(null);
  const stripeCardCvcRef = useRef<StripeCardCvcElement | null>(null);

  const {
    style,
    title,
    subtitle,
    linkedPlan,
    linkedPlanLinkText,
    linkedPlanLink,
    features,
    learnMoreLink,
    contactSale,
    options,
    agreement,
    buttonText,
  } = currentPlan;

  useEffect(() => {
    (async () => {
      if (
        !stripePublishableKey ||
        !cardNumberRef.current ||
        !cardExpiryRef.current ||
        !cardCvcRef.current
      ) {
        return;
      }

      if (!stripe) {
        try {
          loadStripe.setLoadParameters({ advancedFraudSignals: false });
          setStripe(await loadStripe(stripePublishableKey));
        } catch {}
        return;
      }

      const elements = stripe?.elements();

      if (!stripe || !elements) return;

      const style = {
        base: {
          fontSize: "15px",
          fontFamily: `${roboto.style.fontFamily},"Roboto","helvetica neue",sans-serif`,
        },
        invalid: {
          color: "#ac334b",
          iconColor: "#ac334b",
        },
      };
      const cardNumber = elements.create("cardNumber", {
        style,
      });
      stripeCardNumberRef.current = cardNumber;
      const cardExpiry = elements.create("cardExpiry", {
        style,
      });
      stripeCardExpiryRef.current = cardExpiry;
      const cardCvc = elements.create("cardCvc", {
        style,
      });
      stripeCardCvcRef.current = cardCvc;

      cardNumber.mount(cardNumberRef.current);
      cardNumber.on("change", (event) => {
        setCardNumberComplete(event.complete);
        setCardNumberError(!!event.error);
      });
      cardExpiry.mount(cardExpiryRef.current);
      cardExpiry.on("change", (event) => {
        setCardExpiryComplete(event.complete);
        setCardExpiryError(!!event.error);
      });
      cardCvc.mount(cardCvcRef.current);
      cardCvc.on("change", (event) => {
        setCardCvcComplete(event.complete);
        setCardCvcError(!!event.error);
      });
    })();
  }, [setStripe, stripe]);

  useEffect(() => {
    if (
      stripe &&
      stripeCardNumberRef.current &&
      cardNumberComplete &&
      cardExpiryComplete &&
      cardCvcComplete
    ) {
      stripe.createToken(stripeCardNumberRef.current, {}).then((result) => {
        if (result.error) {
          throw result.error;
        } else {
          setCardCountry(result.token?.card?.country || "");
        }
      });
    } else {
      setCardCountry("");
    }
  }, [cardCvcComplete, cardExpiryComplete, cardNumberComplete, stripe]);

  useEffect(() => {
    (async () => {
      setCouponError("");
      const plan = selectedOption.stripePlan;
      setCoupon(null);
      setCouponError("");
      if (debouncedCouponCode) {
        setCouponValidating(true);
        try {
          const response = await fetchApi("/api/v2/stripe/validate-coupon", {
            method: "POST",
            body: JSON.stringify({
              coupon: debouncedCouponCode,
              plan,
            }),
          });
          if (response.errors?.length || !response.coupon?.valid) {
            setCouponError("Invalid coupon!");
          }
          setCoupon(response.coupon);
        } catch {
          setCouponError("Invalid coupon!");
        } finally {
          setCouponValidating(false);
        }
      }
    })();
  }, [debouncedCouponCode, selectedOption.stripePlan]);

  useEffect(() => {
    if (phoneNumber) {
      setPhoneNumberError(!phoneNumber.match(PHONE_NUMBER_REGEX));
    } else {
      setPhoneNumberError(false);
    }
  }, [phoneNumber]);

  const hasTax = TAX_COUNTRY.includes(cardCountry);

  const submitButtonEnabled =
    !submiting &&
    !couponError &&
    !couponValidating &&
    phoneNumber &&
    !phoneNumberError &&
    cardNumberComplete &&
    cardExpiryComplete &&
    cardCvcComplete &&
    agreed;

  const submit = useCallback(() => {
    (async () => {
      if (submiting || !stripe || !stripeCardNumberRef.current) {
        return;
      }

      setSubmiting(true);

      try {
        const tokenData = {
          name: account?.name,
          address_line1: account?.address || "",
          address_city: account?.city || "",
          address_zip: account?.postalcode || "",
          address_country: account?.country || "",
        };
        const tokenResponse = await stripe.createToken(
          stripeCardNumberRef.current,
          tokenData
        );
        if (tokenResponse.error) {
          setNotification(
            "Sorry, we are not able to charge you this time. Please try again later.",
            "error"
          );
          setSubmiting(false);
          return;
        }

        await fetchApi("/api/v2/stripe/charge", {
          method: "POST",
          body: JSON.stringify({
            ...tokenResponse.token,
            coupon: couponCode,
            phone: phoneNumber,
            plan: selectedOption.stripePlan || currentPlan.id,
          }),
        });

        let message =
          "Your payment is being processed and your full access to Insights will be activated in a few minutes. Thank you for your patience.";
        if (currentPlan.id === "corporate-paid") {
          message =
            "Your payment is being processed. You will receive an email once your Corporate Solutions plan is activated. Thank you for your patience.";
        }
        setNotification(message, "success");
        // TODO:
        // if (window.fpr) {
        //     const email = get(this, 'currentUser.email');
        //     window.fpr('referral', { email });
        // }

        setCouponCode("");
        setPhoneNumber("");
        stripeCardNumberRef.current.clear();
      } catch {
        setNotification(
          "Sorry, we are not able to charge you this time. Please try again later.",
          "error"
        );
      } finally {
        setSubmiting(false);
      }
    })();
  }, [
    account?.address,
    account?.city,
    account?.country,
    account?.name,
    account?.postalcode,
    couponCode,
    currentPlan.id,
    phoneNumber,
    selectedOption.stripePlan,
    setNotification,
    stripe,
    submiting,
  ]);

  const price = useMemo(() => {
    let base = selectedOption.price;

    if (hasTax) {
      base = base * (1 + TAX_RATE);
    }

    if (coupon) {
      if (coupon.percent_off) {
        base = base - (base * coupon.percent_off) / 100;
      } else if (coupon.amount_off) {
        base = base - coupon.amount_off / 100;
      }
    }

    return base;
  }, [coupon, hasTax, selectedOption.price]);

  return (
    <>
      <div
        id="account-upgrade-form"
        className="preload text color css text-jade"
      />
      <CustomCard
        className={twMerge(
          "bg bg-gradient-to-b",
          style === "denim" && "from-active-black to-denim",
          style === "success" && "from-smart-teal to-jade",
          "p-[20px] md:p-[40px] md:pt-[47px]",
          className
        )}
      >
        <div className="flex flex-col md:flex-row space-y-[20px] md:space-y-0 md:space-x-[30px]">
          <div className="flex-1 flex flex-col text-white">
            <div className="text-[38px] font-[900]">{title}</div>
            <div className="text-[22px]">{subtitle}</div>
            <div className="text-[14px]">{linkedPlanLinkText}</div>
            {!hideOtherPlan && (
              <div
                className="text-[14px] underline cursor-pointer leading-[1.6]"
                onClick={async () => {
                  const plan = await linkedPlan();
                  setCurrentPlan(plan.default);
                  setSelectedOption(plan.default.options[0]);
                }}
              >
                {linkedPlanLink}
              </div>
            )}
            <div className="flex-1 mt-[38px] text-[18px] leading-[36px]">
              {features?.map?.((feature: any) => (
                <div key={feature.title} className="">
                  <FontAwesomeIcon
                    icon={faCheck}
                    className={twMerge(
                      "text-smart-teal font-bold",
                      "rounded-full bg-white min-w-[20px] max-w-[20px] min-h-[20px] p-[3px]",
                      "mr-[13px]"
                    )}
                  />
                  {feature.title}
                </div>
              ))}
              <div className="mt-[22px] cursor-pointer">
                <a
                  href={learnMoreLink}
                  target="_blank"
                  rel="noreferrer nofollow"
                  className="hover:text-white"
                >
                  <span className="underline">Learn More</span> »
                </a>
              </div>
            </div>
            <div>{contactSale}</div>
          </div>
          <div className="w-full md:w-[318px] px-[22px] pt-[27px] pb-[31px] rounded-lg bg-white">
            <div>
              {options.length > 1 && (
                <div className="mb-[1px]">Choose your plan</div>
              )}
              <div className="flex flex-col space-y-[13px]">
                {options.map((option: any) => {
                  const selected = selectedOption.id === option.id;
                  const price = option.price;
                  return (
                    <div
                      key={option.id}
                      className={twMerge(
                        "border border-solid border-black/20 rounded",
                        "px-[21px] py-[12px]",
                        "flex space-x-[15px]",
                        "relative",
                        options.length === 1 &&
                          selected &&
                          style === "denim" &&
                          "bg-smart-teal/20 py-[18px]",
                        options.length === 1 &&
                          selected &&
                          style === "success" &&
                          "bg-jade/20"
                      )}
                    >
                      {options.length > 1 && (
                        <div
                          className={twMerge(
                            "cursor-pointer",
                            "w-[22px] h-[22px] self-center rounded-full",
                            !selected && "border border-solid border-black/20",
                            selected && style === "denim" && "bg-smart-teal",
                            selected && style === "success" && "bg-jade"
                          )}
                          onClick={() => setSelectedOption(option)}
                        />
                      )}
                      <div
                        className="cursor-pointer"
                        onClick={() => setSelectedOption(option)}
                      >
                        <div className="text-[16px] font-bold">
                          {option.title}
                        </div>
                        <div>
                          USD {numeral(price).format("0,0.00")}/
                          {option.priceScope}
                        </div>
                      </div>
                      {option.tooltip && (
                        <div
                          className={twMerge(
                            "absolute top-[-15px] right-[-10px] px-[15px] py-[4px]",
                            "rounded-full text-[14px] text-white",
                            style === "denim" && "bg-smart-teal",
                            style === "success" && "bg-jade"
                          )}
                        >
                          {option.tooltip}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="mt-[44px]">
              <div className="mb-[1px]">Promo Code (Optional)</div>
              <div className="relative">
                <input
                  className={twMerge(
                    "w-full outline-none h-[40px] rounded border border-solid p-[10px]",
                    couponError
                      ? "border-bearish-red text-bearish-red"
                      : "border-black/20"
                  )}
                  onChange={(event) => setCouponCode(event.target.value)}
                />
                {couponValidating && (
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="animate-spin absolute top-[15px] right-[10px]"
                  />
                )}
              </div>
              {coupon?.name && (
                <div className="mt-[10px] text-[13px] text-bullish-green px-[10px]">
                  {coupon.name}
                </div>
              )}
            </div>
            <div className="mt-[18px]">
              <div className="mb-[1px]">Mobile Number (for verification)</div>
              <input
                className={twMerge(
                  "w-full h-[40px] rounded border border-solid p-[10px]",
                  phoneNumberError
                    ? "border-bearish-red text-bearish-red"
                    : "border-black/20"
                )}
                onChange={(event) => setPhoneNumber(event.target.value)}
              />
            </div>
            <div className="mt-[18px]">
              <div className="mb-[1px]">Card Number</div>
              <div
                className={twMerge(
                  "border border-solid rounded p-[10px] h-[40px]",
                  cardNumberError ? "border-bearish-red" : "border-black/20"
                )}
              >
                <div ref={cardNumberRef} />
              </div>
            </div>
            <div className="mt-[18px] flex space-x-[10px]">
              <div className="flex-1">
                <div className="mb-[1px]">Expiry Date</div>
                <div
                  className={twMerge(
                    "border border-solid rounded p-[10px] h-[40px]",
                    carExpiryError ? "border-bearish-red" : "border-black/20"
                  )}
                >
                  <div ref={cardExpiryRef} />
                </div>
              </div>
              <div className="flex-1">
                <div className="mb-[1px]">Security Code</div>
                <div
                  className={twMerge(
                    "border border-solid rounded p-[10px] h-[40px]",
                    cardCvcError ? "border-bearish-red" : "border-black/20"
                  )}
                >
                  <div ref={cardCvcRef} />
                </div>
              </div>
            </div>
            <div
              className={twMerge(
                "mt-[40px] flex text-[24px] font-bold",
                "pb-[10px] border-0 border-b border-solid border-black/20"
              )}
            >
              <div className="flex-1">Total:</div>
              <div>
                USD {numeral(price).format("0,0.00")}
                {hasTax && (
                  <div className="text-[15px] text-right">(w/GST)</div>
                )}
              </div>
            </div>
            <div className="mt-[16px] flex">
              <input
                id="payment-agreement"
                type="checkbox"
                className="text-[20px] h-[20px] w-[20px] rounded-none cursor-pointer checked:text-smart-teal focus:ring-0"
                onChange={(event) => setAgreed(event.target.checked)}
              />
              <label
                htmlFor="payment-agreement"
                className="ml-[12px] cursor-pointer"
              >
                {agreement}
              </label>
            </div>
            <div className="mt-[20px]">
              <button
                className={twMerge(
                  "border border-solid border-smart-teal",
                  style === "denim" && "bg-smart-teal",
                  style === "success" && "bg-jade",
                  "rounded px-[22px] py-[7px] w-full",
                  "text-[18px] text-white font-medium",
                  !submitButtonEnabled && "opacity-50",
                  "relative"
                )}
                disabled={!submitButtonEnabled}
                onClick={submit}
              >
                {submiting && (
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="animate-spin mr-[10px]"
                  />
                )}
                {buttonText}
              </button>
            </div>
          </div>
        </div>
      </CustomCard>
    </>
  );
};

export default AccountUpgradeForm;
